import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { adoptLibraryTemplate, getUserPracticeById } from "../../api";
import "../Accounts/acc-list.css";

/**
 * AdoptTemplateModal — takes a copy of a library template into the practice.
 *
 * Adoption is a copy, not a subscription: the new template is independent from
 * the moment it is created, and a later change to the library template never
 * reaches it. That is stated plainly in the modal because it is the thing users
 * most reasonably assume works the other way.
 *
 * Visual conventions: /CLAUDE.md §1.8 Modals (header tint, h6 title, footer
 * order) and §1.4 Buttons.
 */
export default function AdoptTemplateModal({ show, onHide, libraryTemplate, onAdopted }) {
  const [practices, setPractices] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loadingPractices, setLoadingPractices] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return;
    setError("");
    setSubmitting(false);

    const token = sessionStorage.getItem("user");
    if (!token) return;

    let cancelled = false;
    setLoadingPractices(true);
    try {
      const decoded = jwtDecode(token);
      getUserPracticeById(decoded._id)
        .then((data) => {
          if (cancelled) return;
          const list = Array.isArray(data) ? data : [];
          setPractices(list);
          // One practice is the common case — pre-select it so adopting is a
          // single click rather than a checkbox plus a click.
          setSelected(list.length === 1 ? [list[0].practiceNumber] : []);
        })
        .catch((err) => !cancelled && setError(err.message || "Could not load practices."))
        .finally(() => !cancelled && setLoadingPractices(false));
    } catch {
      setLoadingPractices(false);
      setError("Could not identify the signed-in user.");
    }
    return () => { cancelled = true; };
  }, [show]);

  // A template tagged for specific disciplines only goes into a practice of
  // one of those disciplines. The server enforces the same rule; this keeps
  // the wrong practices from being offered in the first place.
  const taggedCodes = (libraryTemplate?.templateDisciplines || [])
    .map((d) => String(d.disciplineCode || ""))
    .filter(Boolean);
  const practiceAllowed = (prac) =>
    taggedCodes.length === 0 || taggedCodes.includes(String(prac.practiceDisciplineCode || ""));
  const eligible = practices.filter(practiceAllowed);
  const ineligible = practices.filter((p) => !practiceAllowed(p));

  useEffect(() => {
    // Drop any pre-selected practice the template does not cover.
    setSelected((prev) => prev.filter((n) => eligible.some((p) => p.practiceNumber === n)));
  }, [libraryTemplate, practices]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePractice = (practiceNumber) => {
    setSelected((prev) =>
      prev.includes(practiceNumber)
        ? prev.filter((p) => p !== practiceNumber)
        : [...prev, practiceNumber],
    );
  };

  const handleAdopt = async () => {
    if (selected.length === 0) {
      setError("Choose at least one practice.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const result = await adoptLibraryTemplate(String(libraryTemplate._id), selected);
      onAdopted(result);
    } catch (err) {
      setError(err.message || "Failed to adopt the template.");
      setSubmitting(false);
    }
  };

  if (!libraryTemplate) return null;

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header
        closeButton
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">
          Use Template: {libraryTemplate.templateTitle}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="posted-invoice-info mb-3">
          <p className="mb-1">
            This makes your own copy of{" "}
            <span className="fw-semibold">{libraryTemplate.templateTitle}</span>. Edit it
            however you like — it is yours from the moment you take it.
          </p>
          <p className="mb-0 small text-muted">
            Later changes to the library version will not affect your copy. It arrives as a
            Draft so you can review it before anyone uses it.
          </p>
        </div>

        {libraryTemplate.templateDescription && (
          <p className="small text-muted">{libraryTemplate.templateDescription}</p>
        )}

        <Form.Group>
          <Form.Label>Add to</Form.Label>
          {loadingPractices ? (
            <div className="d-flex align-items-center gap-2">
              <Spinner size="sm" animation="border" variant="primary" />
              <span className="small text-muted">Loading practices…</span>
            </div>
          ) : practices.length === 0 ? (
            <p className="small text-muted mb-0">No practices found for your account.</p>
          ) : (
            <>
              {eligible.length === 0 && (
                <p className="small text-muted mb-1">
                  None of your practices match this template's discipline.
                </p>
              )}
              {eligible.map((prac) => (
                <Form.Check
                  key={prac.practiceNumber}
                  id={`adopt-prac-${prac.practiceNumber}`}
                  type="checkbox"
                  label={prac.practiceName}
                  checked={selected.includes(prac.practiceNumber)}
                  onChange={() => togglePractice(prac.practiceNumber)}
                />
              ))}
              {ineligible.map((prac) => (
                <Form.Check
                  key={prac.practiceNumber}
                  id={`adopt-prac-${prac.practiceNumber}`}
                  type="checkbox"
                  disabled
                  checked={false}
                  readOnly
                  label={`${prac.practiceName} (different discipline)`}
                />
              ))}
            </>
          )}
        </Form.Group>

        {error && <Alert variant="danger" className="mt-3 mb-0 py-2 small">{error}</Alert>}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="primary"
          onClick={handleAdopt}
          disabled={submitting || loadingPractices || selected.length === 0}
          style={{ minWidth: 140 }}
        >
          {submitting ? (
            <Spinner size="sm" animation="border" />
          ) : (
            <>
              <i className="fa-solid fa-copy me-2" />
              Use Template
            </>
          )}
        </Button>
        <Button
          variant="outline-secondary"
          onClick={onHide}
          disabled={submitting}
          style={{ minWidth: 140 }}
        >
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
